import { Component, OnInit, inject, signal, OnDestroy } from '@angular/core';
import { Subject, finalize, takeUntil } from 'rxjs';
import { Payment } from '../../shared/models/payment.model';
import { PaymentService } from '../../shared/util/payment-api.service';
import { ToastService } from '@black/ui';

@Component({
  selector: 'black-payment-management',
  templateUrl: './payment-management.component.html',
  standalone: false,
})
export class PaymentManagementComponent implements OnInit, OnDestroy {
  private readonly paymentService = inject(PaymentService);
  readonly toast = inject(ToastService);
  private readonly destroy$ = new Subject<void>();

  allPayments: Payment[] = [];
  total = 0;
  page = 1;
  limit = 10;
  loading = false;
  processing = signal(false);

  filterTab: 'ALL' | 'PENDING' | 'APPROVED' = 'ALL';
  searchTerm = '';

  selectedPayment: Payment | null = null;
  showInfoModal = false;
  showApproveConfirm = false;

  readonly Math = Math;

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    this.paymentService
      .getAll(this.page, this.limit)
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (res) => {
          this.allPayments = res.docs ?? [];
          this.total = res.totalDocs ?? 0;
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to load payments.',
            'error',
          );
        },
      });
  }

  get filteredPayments(): Payment[] {
    let list = this.allPayments;

    if (this.filterTab === 'PENDING') {
      list = list.filter((p) => !p.isApproved);
    } else if (this.filterTab === 'APPROVED') {
      list = list.filter((p) => !!p.isApproved);
    }

    if (this.searchTerm.trim()) {
      const q = this.searchTerm.trim().toLowerCase();
      list = list.filter((p) => {
        const idMatch = p._id?.toLowerCase().includes(q);
        const nameMatch = this.getStudentName(p).toLowerCase().includes(q);
        const contactMatch = this.getStudentContact(p).toLowerCase().includes(q);
        const emailMatch = this.getStudentEmail(p).toLowerCase().includes(q);
        const modeMatch = p.modeOfPayment?.toLowerCase().includes(q);
        const itemMatch = p.itemTitle?.toLowerCase().includes(q);
        return idMatch || nameMatch || contactMatch || emailMatch || modeMatch || itemMatch;
      });
    }

    return list;
  }

  get pendingCount(): number {
    return this.allPayments.filter((p) => !p.isApproved).length;
  }

  get approvedCount(): number {
    return this.allPayments.filter((p) => !!p.isApproved).length;
  }

  getStudentName(p: Payment): string {
    return p.billingInformation?.name || p.student?.name || p.student?.username || '—';
  }

  getStudentContact(p: Payment): string {
    return p.billingInformation?.contactNumber || p.student?.contactNumber || '';
  }

  getStudentEmail(p: Payment): string {
    return p.student?.email || p.billingInformation?.email || p.student?.username || '';
  }

  getSlipUrl(p: Payment): string {
    const slip = p.bankData?.slip;
    return typeof slip === 'string' ? slip : slip?.path || '';
  }

  isSlipPdf(p: Payment): boolean {
    const url = this.getSlipUrl(p).toLowerCase();
    const mime = p.bankData?.slip?.mimeType?.toLowerCase() || '';
    return url.endsWith('.pdf') || mime.includes('pdf');
  }

  onPageChange(p: number): void {
    this.page = p;
    this.load();
  }

  openApproveConfirm(payment: Payment): void {
    this.selectedPayment = payment;
    this.showApproveConfirm = true;
  }

  closeApproveConfirm(): void {
    this.showApproveConfirm = false;
  }

  confirmApprove(): void {
    if (!this.selectedPayment?._id) return;
    this.processing.set(true);
    this.paymentService
      .approvePayment(this.selectedPayment._id)
      .pipe(
        finalize(() => this.processing.set(false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.toast.showToast('Payment approved successfully!', 'success');
          this.showApproveConfirm = false;
          if (this.showInfoModal && this.selectedPayment) {
            this.selectedPayment.isApproved = true;
          }
          this.load();
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to approve payment.',
            'error',
          );
        },
      });
  }

  openInfo(payment: Payment): void {
    this.selectedPayment = payment;
    this.showInfoModal = true;
  }

  closeInfo(): void {
    this.showInfoModal = false;
  }

  refresh(): void {
    this.load();
  }
}
